import { ConfluencePageAri } from '@atlassian/ari';
import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
import { getCloudId } from '@databases/database-state';
import { logError, logWarning } from '@databases/logs';
import { DefaultRootState } from '@databases/redux';
import { loadContent } from '@databases/redux/connections/actions';
import { GuardedType, ObjectMode } from '@databases/types';
import {
  BlogpostReference,
  BlogpostReferenceOrContentId,
  Content,
  ContentRepresentation,
  OptionalCreatePageReference,
  OptionalCreatePageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  DatabaseWithoutEntries,
  Hyperlink,
  ImportedFileType,
  RawDatabaseValue,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { cloneDeep, isEqual, omit } from 'lodash-es';

import { DATA_LINKED_RESOURCE_ID_ATTR } from '../../confluence/content';
import {
  getContentProperty,
  updateContentProperty,
} from '../../confluence/contentProperty';
import { retry } from '../../confluence/fetch';
import { createPage } from '../../confluence/page';
import { getTemplateById } from '../../confluence/template';
import {
  blogpostToReference,
  contentToReference,
  getContentKey,
  isBlogpostReference,
  isContent,
  isPageReference,
  pageToReference,
  serializeBlogpostReference,
  serializePageReference,
} from '../../confluence/utils';
import { DATABASE_ENTRY_MACRO_KEY } from '../../constants';
import { cheerioIterator, parseStorageFormat } from '../../lib/dom';
import { deserializeMacroParameters, findMacrosByKey, serializeMacro } from '../../macro';
import {
  CONTENT_EXPANSIONS,
  addContentToList,
  loadContentById,
} from '../../redux/content/actions';
import {
  selectContentByIdOptional,
  selectContentRefOptional,
} from '../../redux/content/selectors';
import { selectBaseUrl } from '../../redux/context/selectors';
import { matchViewPageOrBlogpostPath } from '../../urls';
import { encodeHtml, generateId } from '../../utils';
import { DatabaseChange, DatabaseChangeType, MultiValueChangeType } from '../changesets';
import { FieldType } from '../field-types';
import { isDefaultEntry } from '../utils';

export async function createPageWithOptions({
  templateId,
  entryUrl,
  spaceKey,
  parentId,
  contentTitle,
}: {
  templateId: string | null;
  entryUrl: string | null;
  spaceKey: string;
  parentId: string;
  contentTitle: string;
}): Promise<Content | undefined> {
  const contentSpecificCreateInput = [];

  entryUrl &&
    contentSpecificCreateInput.push({ key: 'headerContentUrl', value: entryUrl });
  templateId && contentSpecificCreateInput.push({ key: 'templateId', value: templateId });

  const CreatePageWithOptionsMutation = `
    mutation CreatePageWithOptionsMutation($parentId: ID!, $title: String, $spaceKey: String!, $contentSpecificCreateInput: [ContentSpecificCreateInput!]) {
      createContentContextual(
        input: {
          parentId: $parentId,
          spaceKey: $spaceKey,
          title: $title,
          status: CURRENT,
          type: "page",
          contentSpecificCreateInput: $contentSpecificCreateInput
        }
      ) {
        extensions {
          key
          value
        }
        history {
          createdDate
          latest
        }
        macroRenderedOutput {
          key
        }
        operations {
          operation
          targetType
        }
        space {
          id
          key
          name
          status
          type
        }
        id
        status
        title
        type
        links {
          base
          collection
          context
          download
          editui
          self
          tinyui
          webui
        }
      }
    }
  `;

  let content: Content | undefined = undefined;

  try {
    const response = await fetch('/cgraphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify([
        {
          query: CreatePageWithOptionsMutation,
          variables: {
            parentId,
            title: contentTitle,
            contentSpecificCreateInput: contentSpecificCreateInput,
            spaceKey: spaceKey,
          },
        },
      ]),
    });
    const json = await response.json();
    if (!json || !Array.isArray(json) || !json[0]?.data?.createContentContextual) {
      let error;
      if (json && Array.isArray(json) && json[0]?.errors?.length > 0) {
        error = new Error(
          `Invalid createContentContextual response: ${json[0].errors[0].message}`
        );
      } else {
        error = new Error('Invalid createContentContextual response');
      }
      throw error;
    }
    content = {
      ...json[0].data.createContentContextual,
      _links: json[0].data.createContentContextual.links,
    };
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error: unknown) {
    if (error instanceof Error) {
      logWarning('Error creating page', error);
      throw error;
    }
  }
  return content;
}

/**
 * Creates a new page under the specified parent page of a page field. Depending on what page template is configured
 * for the page field, creates an empty page, a page with an entry macro, or a page from a Confluence template/blueprint.
 * If the page is created for the default value of the field, it is always created empty.
 */
export async function createEntryPage(
  context: {
    database: DatabaseWithoutEntries<ObjectMode>;
    field: DatabaseField;
    entry: DatabaseEntry;
    entryUrl?: string | undefined;
  },
  page: OptionalCreatePageReference
): Promise<Content> {
  const { database, field, entry, entryUrl } = context;
  const templateRef = field.settings.pageTemplate;
  let body;
  if (
    isDefaultEntry(entry) ||
    templateRef.templateId === CUSTOM_TEMPLATE_EMPTY.templateId
  ) {
    body = '';
  } else if (templateRef.templateId === CUSTOM_TEMPLATE_ENTRY_MACRO.templateId) {
    body = entryUrl
      ? `<a href="${encodeHtml(
          entryUrl
        )}" data-card-appearance="embed" data-layout="wide">${encodeHtml(entryUrl)}</a>`
      : '';
  } else {
    const template = await getTemplateById(templateRef.templateId, ['body.storage']);
    // If there are "Add current page as new entry" macros linking the current database in the template,
    // link them to the current entry to avoid creating another entry when the page is loaded.
    const el = parseStorageFormat(template.body!.storage!.value);
    const macros = findMacrosByKey(el, DATABASE_ENTRY_MACRO_KEY);
    for (const macro of cheerioIterator(macros)) {
      const params = deserializeMacroParameters(
        DATABASE_ENTRY_MACRO_KEY,
        macro,
        template.space?.key ?? ''
      );
      if (
        params.databaseSpace.key === database.space.key &&
        params.databaseId === database.id &&
        params.create
      ) {
        macro.replaceWith(
          serializeMacro(DATABASE_ENTRY_MACRO_KEY, {
            ...omit(params, ['create']),
            entryId: entry.id,
          })
        );
      }
    }
    body = el.html()!;
  }

  const result = await createPage(
    {
      title: page.contentTitle,
      space: { key: page.spaceKey },
      ancestors: page.create!.parentPageId ? [{ id: page.create!.parentPageId }] : [],
      body: { storage: { representation: ContentRepresentation.STORAGE, value: body } },
    },
    [...CONTENT_EXPANSIONS, 'metadata.properties.content-appearance-published']
  );

  if (
    result.metadata?.properties?.['content-appearance-published']?.value !== 'default'
  ) {
    // Set the page to fixed-width mode. Setting this directly in the create request seems to frequently
    // fail with a 500 UnexpectedRollbackException, probably since Confluence sometimes sets the content
    // property itself.
    try {
      await retry(
        async ({ attempt }) => {
          let contentAppearanceVersion;
          if (attempt > 1) {
            contentAppearanceVersion =
              (await getContentProperty(result.id, 'content-appearance-published'))
                ?.version.number ?? 0;
          } else {
            contentAppearanceVersion =
              result.metadata?.properties?.['content-appearance-published']?.version
                .number ?? 0;
          }

          await updateContentProperty(result.id, {
            key: 'content-appearance-published',
            value: 'default',
            version: { number: contentAppearanceVersion + 1 },
          });
        },
        (err) => err._orderlyStatus === 409
      );
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (err: unknown) {
      if (err instanceof Error) {
        logError('Error setting content appearance', err);
      }
    }
  }

  return result;
}

/**
 * Converts a page value to a list of page references. Empty page values that have been added by the user through the UI
 * but are not filled out yet are omitted from the list. Page references by ID are resolved, if they are not in the state,
 * they are omitted.
 */
export function normalizePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>,
  state: DefaultRootState | undefined
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReference | BlogpostReference)
> {
  return value.flatMap((v) => {
    const normalized = resolveSinglePageLinkValue(v, state);
    return 'contentTitle' in normalized ? [normalized] : [];
  });
}

export function filterValidPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReferenceOrContentId | BlogpostReferenceOrContentId)
> {
  return value.flatMap((v) => {
    if ('contentTitle' in v || 'contentId' in v) {
      return [v];
    }
    return [];
  });
}

function getHyperlinkRepresentation(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>,
  state: DefaultRootState | undefined
): Array<Hyperlink & { _id: string }> {
  const resolvedRefs = value
    .map((v) => {
      if (!v || !state) {
        return undefined;
      }
      if ('create' in v && !v.create) {
        return v;
      }

      if ('contentId' in v) {
        const resolvedRef = selectContentByIdOptional(state, {
          contentId: v.contentId,
        });
        if (resolvedRef) {
          if (
            isContent(resolvedRef) &&
            (resolvedRef.type === 'page' || resolvedRef.type === 'blogpost')
          ) {
            return contentToReference(resolvedRef);
          }
        }
      } else {
        return undefined;
      }
    })
    .filter((v) => v !== undefined && 'url' in v);

  return resolvedRefs.map((v) => ({
    _id: generateId(),
    url: v.url,
    label: v.contentTitle,
  }));
}

/**
 * If the given page value is a page ID reference, attempts to resolve it and return a page reference. If the value is not
 * a page ID reference or the page with the given ID is not in the store, the value is returned unchanged.
 */
export function resolveSinglePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0],
  state: DefaultRootState | undefined
): RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] {
  if ('contentId' in value) {
    const content =
      state && selectContentByIdOptional(state, { contentId: value.contentId });
    if (content?.type === 'page') {
      return { ...pageToReference(content), _id: value._id };
    } else if (content?.type === 'blogpost') {
      return { ...blogpostToReference(content), _id: value._id };
    }
  }
  return value;
}

/**
 * Returns true if the given page value is not empty. Empty page values are added by the user through the UI
 * but have not been filled with a page reference yet.
 */
function isNonEmptyPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0]
): value is RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
  (OptionalCreatePageReference | BlogpostReference | { contentId: string }) {
  return 'contentTitle' in value || 'contentId' in value;
}

/**
 * Returns true if the given page link field value represents an existing page or blog post, rather than one to be created or an empty
 * value.
 */
export function isExistingPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0]
): value is RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
  (OptionalCreatePageReference | BlogpostReference) {
  return (
    ('contentTitle' in value || 'contentId' in value) &&
    (!isPageReference(value) || !value.create)
  );
}

const pageLinkFieldType: FieldType<DatabaseFieldType.PAGE_LINK> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  supportedImportTypes: [ImportedFileType.HTML],

  serializeValue: ({ value }, state) => ({
    body: {
      html: normalizePageLinkValue(value, state)
        .map((v) => {
          if (isBlogpostReference(v)) {
            return serializeBlogpostReference(v, true);
          } else {
            return serializePageReference(v, true);
          }
        })
        .join('<br />'),
    },
  }),

  deserializeValue: (params, bodyEl, database) => {
    return [...cheerioIterator(bodyEl.find('ri\\:page,ri\\:blog-post'))].map((el) => {
      if (el.is('ri\\:blog-post')) {
        return {
          type: 'blogpost',
          _id: generateId(),
          ari: el.attr('ri:ari') ?? '',
          spaceKey: el.attr('ri:space-key') || database.space.key,
          contentTitle: el.attr('ri:content-title') ?? '',
          postingDay: el.attr('ri:posting-day') ?? '',
        };
      } else {
        return {
          type: 'page',
          _id: generateId(),
          ari: el.attr('ri:ari') ?? '',
          spaceKey: el.attr('ri:space-key') || database.space.key,
          contentTitle: el.attr('ri:content-title') ?? '',
        };
      }
    });
  },

  renderExportView: ({ value }, state) => {
    return normalizePageLinkValue(value, state)
      .map(
        (v) =>
          `<ac:link ac:card-appearance="inline">${
            isBlogpostReference(v)
              ? serializeBlogpostReference(v)
              : serializePageReference(v)
          }</ac:link>`
      )
      .join('<br />');
  },

  parseFromHtml: (el, context, state) => {
    const baseUrl = selectBaseUrl(state);

    return [...cheerioIterator(el.find('a[href]'))].flatMap((a) => {
      const href = a.attr('href') ?? '';
      if (href.startsWith(baseUrl)) {
        const id = a.attr(DATA_LINKED_RESOURCE_ID_ATTR);
        const match = matchViewPageOrBlogpostPath(href, id);
        if (match) {
          return [
            {
              _id: generateId(),
              contentId: match.contentId,
              ari: ConfluencePageAri.create({
                siteId: getCloudId()!,
                pageId: match.contentId,
              }).toString(),
            },
          ];
        }
      }
      return [];
    });
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) => value.filter(isNonEmptyPageLinkValue).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    const getKey = (v: GuardedType<typeof isNonEmptyPageLinkValue>) =>
      'contentTitle' in v ? getContentKey(v) : v.contentId;
    return isEqual(
      value1.filter(isNonEmptyPageLinkValue).map(getKey),
      value2.filter(isNonEmptyPageLinkValue).map(getKey)
    );
  },

  getStringRepresentation: ({ value }, state) =>
    normalizePageLinkValue(value, state)
      .map((v) => v.contentTitle)
      .join('\n'),

  getReferencedAttachments: () => [],

  getReferencedContent: ({ value }) =>
    filterValidPageLinkValue(value).filter((v) => isExistingPageLinkValue(v)),

  loadDependencies: ({ field, value }, helper) => {
    const dispatch = helper.dispatch;
    for (const v of value) {
      if ('contentId' in v && v.contentId) {
        dispatch(loadContentById(v.contentId));
      }

      const ari = getConfluencePageAriFromContentRef(v);
      if (ari) {
        dispatch(
          loadContent({
            ari,
            connectionId: field.id,
          })
        );
      }
    }
  },

  applySideEffects: async ({ value, field, database, entry }, store) => {
    const updatedValue = cloneDeep(value);
    const state = store.getState();
    const changes: DatabaseChange[] = [];
    let idx = 0;
    for (const v of updatedValue) {
      if (!isNonEmptyPageLinkValue(v)) {
        idx++;
        return;
      }

      if ('create' in v) {
        const existingPage = selectContentRefOptional(state, { contentRef: v });
        if (existingPage) {
          // Page has been created by the side effect of another value
          v.spaceKey = existingPage.spaceKey;
          v.contentTitle = existingPage.contentTitle;
          delete v.create;
          changes.push({
            type: DatabaseChangeType.SET_VALUE,
            fieldId: field.id,
            entryId: entry.id,
            value: {
              type: DatabaseFieldType.PAGE_LINK,
              value: updatedValue,
            },
            change: {
              type: DatabaseFieldType.PAGE_LINK,
              delta: {
                type: MultiValueChangeType.EDIT,
                index: idx,
                value: v,
              },
            },
          });
        } else {
          const newPage = await createEntryPage({ database, field, entry }, v);
          store.dispatch(addContentToList(newPage));
          v.spaceKey = newPage.space!.key;
          v.contentTitle = newPage.title;
          delete v.create;
          changes.push({
            type: DatabaseChangeType.SET_VALUE,
            fieldId: field.id,
            entryId: entry.id,
            value: {
              type: DatabaseFieldType.PAGE_LINK,
              value: updatedValue,
            },
            change: {
              type: DatabaseFieldType.PAGE_LINK,
              delta: {
                type: MultiValueChangeType.EDIT,
                index: idx,
                value: v,
              },
            },
          });
        }
      }

      idx++;
    }

    return changes;
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }, state) =>
      normalizePageLinkValue(value, state)
        .map((v) => v.contentTitle)
        .join('\n'),
    [DatabaseFieldType.HYPERLINK]: ({ value }, state) =>
      getHyperlinkRepresentation(value, state),
  },
};

export default pageLinkFieldType;
